import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  downloadAccountingInvoicePDF,
  downloadAccountingPaymentReceiptPDF,
} from "../../utils/generateAccountingInvoicePDF";
import FilteredTableCore from "../../components/Organismos/Table/FilteredTableCore";
import type {
  IHeader,
  IHeaderCellProps,
} from "../../components/Organismos/Table/FilteredTable.types";
import Button from "../../components/staff/Button";
import {
  getOfficeBillingHistory,
  upsertInvoice,
  upsertPayment,
  type BillingHistoryDoc,
} from "../../Services/Billing";
import { formatCalendarDateToString } from "../../utils/dates";
import { formatBillingStatusLabel, isCancelledBillingDoc, BILLING_STATUS } from "../../utils/billingStatus";
import {
  DIRECT_PAYMENT_CODE,
  DIRECT_PAYMENT_DESCRIPTION,
} from "../../utils/billingConstants";
import {
  compareDocsByBusinessDate,
  filterLedgerDocsForTab,
  isDirectPaymentDoc,
  ledgerBusinessDateIso,
  orderLedgerWithNestedPayments,
  type LedgerHistoryTab,
} from "../../utils/billingLedgerUtils";
import type { ISorting } from "../../interfaces/Components/ITable";
import { AccountingModalTabs } from "./accountingModalUi";
import type { AccountingOfficeDetail } from "../../utils/accountingOfficeDetail";
import {
  officeDisplayName,
  officePartyKey,
} from "../../utils/accountingOfficeDetail";
import CreateInvoiceModal from "./CreateInvoiceModal";
import CreatePaymentModal from "./CreatePaymentModal";
import EditInvoiceModal from "./EditInvoiceModal";
import EditPaymentModal from "./EditPaymentModal";
import AccountingInvoiceActionsCell from "./AccountingInvoiceActionsCell";
import SendBillingDocModal from "./SendBillingDocModal";
import CancelBillingDocModal from "./CancelBillingDocModal";
import {
  cancelAccountingInvoice,
  countLinkedPaymentsForCancel,
} from "../../utils/cancelAccountingInvoice";
import { cancelAccountingPayment } from "../../utils/cancelAccountingPayment";
import {
  SECTION_TITLE_CLASS,
  FEE_TYPE_LABEL_CLASS,
} from "./accountingFirmPanels";
import { toastError, toastSuccess } from "../../utils/toast";
import {
  resolveBillingSendRecipients,
  sendAccountingInvoice,
  sendAccountingPayment,
} from "../../utils/sendAccountingDocument";
import {
  EMPTY_BILLING_SEND_RECIPIENTS,
  getSendBillingDisabledReason,
  resolveBillingEmailAddressing,
  type BillingSendRecipients,
} from "../../utils/billingSendRecipients";
import {
  fetchStaffContactOptions,
  type StaffContactOption,
} from "../../utils/fetchStaffContactOptions";
import type { Option } from "../../components/Atomos/DropDown/DDMultiple";
import { useInvoiceAuditLogger } from "../../utils/invoiceAudit";
import { useAuth } from "../../store/AuthContext";
import { resolvePerformedBy } from "../../utils/accountingOfficeBalance";

const TOTALS_TITLE_CLASS =
  "font-decimal-medium text-lg font-medium uppercase leading-none tracking-wide text-gray-font md:text-xl";

const TOTALS_LABEL_CLASS =
  "font-sans text-base font-normal leading-5 text-gray-font md:text-lg";

const TOTALS_VALUE_CLASS =
  "text-lg font-semibold tabular-nums leading-none text-gray-font md:text-xl";

const LEDGER_HISTORY_TABS: { key: LedgerHistoryTab; label: string }[] = [
  { key: "all", label: "All" },
  { key: "annual", label: "Annual" },
  { key: "assessment", label: "Assessment" },
];

const INVOICE_KIND_LABELS: Record<string, string> = {
  membershipDues: "Membership Dues",
  assessment: "Assessment",
  special: "Assessment",
  annualMeeting: "Annual Meeting",
  lateFee: "Late Fee",
  contingency: "Contingency",
  event: "Event",
  other: "Other",
};

function formatMoney(value: unknown): string {
  const num = Number(value ?? 0);
  if (Number.isNaN(num) || num === 0) {
    return "";
  }
  return `$${num.toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

function formatMoneyAlways(value: unknown): string {
  const num = Number(value ?? 0);
  if (Number.isNaN(num)) {
    return "$0.00";
  }
  return `$${num.toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

function buildLedgerTotals(docs: BillingHistoryDoc[]): {
  totalCharged: number;
  totalPaid: number;
  recordCount: number;
} {
  let totalCharged = 0;
  let totalPaid = 0;
  let recordCount = 0;
  for (const d of docs) {
    if (isCancelledBillingDoc(d)) {
      continue;
    }
    recordCount += 1;
    if (d.type === "payment") {
      if (isDirectPaymentDoc(d)) {
        continue;
      }
      totalPaid += Number(d.amount) || 0;
    } else {
      totalCharged += Number(d.total) || 0;
    }
  }
  return { totalCharged, totalPaid, recordCount };
}

const LEDGER_HISTORY_DEFAULT_SORTING: ISorting = {
  field: "ledgerSortAt",
  order: "desc",
};

const LEDGER_DATE_SORT_FIELDS = new Set(["ledgerSortAt", "ledgerDate"]);

export default function AccountingInvoiceHistory({
  office,
  billingRefreshKey = 0,
  onBillingRefresh,
}: {
  office: AccountingOfficeDetail;
  billingRefreshKey?: number;
  onBillingRefresh?: () => void;
}): React.ReactElement {
  const officeId = office.id;
  const firmId = String(office.firm ?? "") || null;
  const { logEvent } = useInvoiceAuditLogger();
  const { loggedPerson, tokenDecoded } = useAuth();
  const [activeTab, setActiveTab] = useState<LedgerHistoryTab>("all");
  const [activeSorting, setActiveSorting] = useState<ISorting>(
    LEDGER_HISTORY_DEFAULT_SORTING,
  );
  const [createOpen, setCreateOpen] = useState(false);
  const [paymentOpen, setPaymentOpen] = useState(false);
  const [paymentInvoiceId, setPaymentInvoiceId] = useState<string | null>(null);
  const [editInvoice, setEditInvoice] = useState<BillingHistoryDoc | null>(
    null,
  );
  const [editPayment, setEditPayment] = useState<BillingHistoryDoc | null>(
    null,
  );
  const [downloadingInvoiceId, setDownloadingInvoiceId] = useState<
    string | null
  >(null);
  const [sendState, setSendState] = useState<{
    docId: string;
    docType: "invoice" | "payment";
    isTest: boolean;
  } | null>(null);
  const [sendRecipients, setSendRecipients] =
    useState<BillingSendRecipients | null>(null);
  const [sendRecipientsLoading, setSendRecipientsLoading] = useState(false);
  const [billingRecipients, setBillingRecipients] =
    useState<BillingSendRecipients>(EMPTY_BILLING_SEND_RECIPIENTS);
  const [staffContacts, setStaffContacts] = useState<StaffContactOption[]>([]);
  const [staffContactsLoading, setStaffContactsLoading] = useState(false);
  const [selectedStaff, setSelectedStaff] = useState<Option[]>([]);
  const [sending, setSending] = useState(false);
  const [cancelState, setCancelState] = useState<{
    docType: "invoice" | "payment";
    doc: BillingHistoryDoc;
    linkedPaymentCount: number;
  } | null>(null);
  const [cancelling, setCancelling] = useState(false);
  const [totals, setTotals] = useState({
    totalCharged: 0,
    totalPaid: 0,
    recordCount: 0,
  });

  const notifyBillingChanged = useCallback(() => {
    onBillingRefresh?.();
  }, [onBillingRefresh]);

  const persistentFilter = useMemo(
    () => ({
      partyKey: { value: officePartyKey(officeId), comparisonType: "EQUAL" },
      type: { value: ["invoice", "payment"], comparisonType: "IN" },
    }),
    [officeId],
  );

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await getOfficeBillingHistory(officeId);
        const filtered = filterLedgerDocsForTab(data, activeTab);
        if (!cancelled) {
          setTotals(buildLedgerTotals(filtered));
        }
      } catch {
        if (!cancelled) {
          setTotals({ totalCharged: 0, totalPaid: 0, recordCount: 0 });
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [officeId, billingRefreshKey, activeTab]);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const recipients = await resolveBillingSendRecipients(office);
        if (!cancelled) {
          setBillingRecipients(recipients);
        }
      } catch {
        if (!cancelled) {
          setBillingRecipients(EMPTY_BILLING_SEND_RECIPIENTS);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [office, billingRefreshKey]);

  const canSendBilling = Boolean(
    resolveBillingEmailAddressing(billingRecipients).toEmail,
  );
  const sendBillingDisabledReason = useMemo(
    () => getSendBillingDisabledReason(billingRecipients),
    [billingRecipients],
  );

  const formatData = useCallback(
    (data: BillingHistoryDoc[]) => {
      if (!Array.isArray(data)) {
        return [];
      }

      const scoped = filterLedgerDocsForTab(data, activeTab);
      const docsForOrder = LEDGER_DATE_SORT_FIELDS.has(activeSorting.field)
        ? [...scoped].sort((a, b) =>
            compareDocsByBusinessDate(a, b, activeSorting.order),
          )
        : scoped;

      const formatted = docsForOrder.map((d) => {
        const ledgerSortAt = ledgerBusinessDateIso(d);
        if (d.type === "payment") {
          const isDirectPayment =
            d.code === DIRECT_PAYMENT_CODE ||
            (!d.reference && (!d.allocations || d.allocations.length === 0));
          const methodLabel = d.method ? `Payment · ${d.method}` : "Payment";
          return {
            ...d,
            ledgerSortAt,
            ledgerDate: formatCalendarDateToString(d.paymentDate ?? "") || "—",
            ledgerType: "Payment",
            reference:
              d.reference ?? (isDirectPayment ? DIRECT_PAYMENT_CODE : "—"),
            description:
              d.description ??
              (isDirectPayment ? DIRECT_PAYMENT_DESCRIPTION : methodLabel),
            charge: "",
            credit: formatMoney(d.amount),
            balance: "",
            status: isCancelledBillingDoc(d)
              ? formatBillingStatusLabel(BILLING_STATUS.cancelled)
              : "",
          };
        }

        const kindLabel =
          INVOICE_KIND_LABELS[d.context?.kind ?? ""] ??
          d.context?.kind ??
          "Invoice";
        const fy = d.context?.fiscalYear ? ` · FY${d.context.fiscalYear}` : "";

        return {
          ...d,
          ledgerSortAt,
          ledgerDate: formatCalendarDateToString(d.issueDate ?? "") || "—",
          ledgerType: "Invoice",
          reference: d.invoiceNumber ?? "—",
          description: `${kindLabel}${fy}`,
          charge: formatMoney(d.total),
          credit: "",
          balance: formatMoney(d.balance),
          status: formatBillingStatusLabel(d.status),
        };
      });

      return orderLedgerWithNestedPayments(docsForOrder, formatted, activeTab);
    },
    [activeTab, activeSorting],
  );

  const handlePreviewEdit = useCallback((invoice: BillingHistoryDoc) => {
    setEditInvoice(invoice);
  }, []);

  const handleDownloadInvoice = useCallback(
    async (invoice: BillingHistoryDoc) => {
      setDownloadingInvoiceId(invoice.id);
      try {
        await downloadAccountingInvoicePDF(invoice, office);
      } catch (error) {
        console.error("Error downloading accounting invoice:", error);
        toastError("Unable to download invoice. Please try again.");
      } finally {
        setDownloadingInvoiceId(null);
      }
    },
    [office],
  );

  const handleAddPayment = useCallback((invoice: BillingHistoryDoc) => {
    setPaymentInvoiceId(invoice.id);
    setPaymentOpen(true);
  }, []);

  const handlePreviewEditPayment = useCallback((payment: BillingHistoryDoc) => {
    setEditPayment(payment);
  }, []);

  const handleDownloadPayment = useCallback(
    async (payment: BillingHistoryDoc) => {
      setDownloadingInvoiceId(payment.id);
      try {
        await downloadAccountingPaymentReceiptPDF(payment, office);
      } catch (error) {
        console.error("Error downloading payment receipt:", error);
        toastError("Unable to download payment receipt. Please try again.");
      } finally {
        setDownloadingInvoiceId(null);
      }
    },
    [office],
  );

  const handleClosePaymentModal = useCallback(() => {
    setPaymentOpen(false);
    setPaymentInvoiceId(null);
  }, []);

  const openSend = useCallback(
    (
      doc: BillingHistoryDoc,
      docType: "invoice" | "payment",
      isTest: boolean,
    ) => {
      setSendState({ docId: doc.id, docType, isTest });
      setSendRecipients(null);
      setSelectedStaff([]);
      setStaffContacts([]);

      if (isTest) {
        setSendRecipientsLoading(false);
        setStaffContactsLoading(true);
        void (async () => {
          try {
            const contacts = await fetchStaffContactOptions();
            setStaffContacts(contacts);
          } catch {
            setStaffContacts([]);
          } finally {
            setStaffContactsLoading(false);
          }
        })();
        return;
      }

      setSendRecipientsLoading(true);
      void (async () => {
        try {
          const recipients = await resolveBillingSendRecipients(office);
          setSendRecipients(recipients);
          setBillingRecipients(recipients);
        } catch {
          setSendRecipients(EMPTY_BILLING_SEND_RECIPIENTS);
        } finally {
          setSendRecipientsLoading(false);
        }
      })();
    },
    [office],
  );

  const handleSend = useCallback(
    (invoice: BillingHistoryDoc) => openSend(invoice, "invoice", false),
    [openSend],
  );
  const handleSendTest = useCallback(
    (invoice: BillingHistoryDoc) => openSend(invoice, "invoice", true),
    [openSend],
  );
  const handleSendPayment = useCallback(
    (payment: BillingHistoryDoc) => openSend(payment, "payment", false),
    [openSend],
  );
  const handleSendTestPayment = useCallback(
    (payment: BillingHistoryDoc) => openSend(payment, "payment", true),
    [openSend],
  );

  const handleCancelSend = useCallback(() => {
    setSendState(null);
    setSendRecipients(null);
    setSelectedStaff([]);
    setStaffContacts([]);
    setStaffContactsLoading(false);
    setSendRecipientsLoading(false);
  }, []);

  const handleOpenCancel = useCallback((doc: BillingHistoryDoc) => {
    void (async () => {
      try {
        if (doc.type === "payment") {
          setCancelState({
            docType: "payment",
            doc,
            linkedPaymentCount: 0,
          });
          return;
        }

        const history = await getOfficeBillingHistory(officeId);
        setCancelState({
          docType: "invoice",
          doc,
          linkedPaymentCount: countLinkedPaymentsForCancel(doc, history),
        });
      } catch {
        toastError("Unable to load billing details. Please try again.");
      }
    })();
  }, [officeId]);

  const handleDismissCancel = useCallback(() => {
    setCancelState(null);
  }, []);

  const handleConfirmCancel = useCallback(async () => {
    if (!cancelState) {
      return;
    }
    setCancelling(true);
    try {
      const cancelDoc = cancelState.doc;
      if (cancelState.docType === "invoice") {
        const actor = resolvePerformedBy(loggedPerson, tokenDecoded);
        const result = await cancelAccountingInvoice(
          cancelDoc,
          office,
          actor,
        );
        notifyBillingChanged();
        setCancelState(null);
        void logEvent({
          billingDocId: cancelDoc.id,
          billingDocType: "invoice",
          partyKey: cancelDoc.partyKey,
          partyId: office.id,
          officeId,
          firmId,
          invoiceNumber: cancelDoc.invoiceNumber,
          contextKind: cancelDoc.context?.kind,
          action: "cancelled",
          detail:
            result.cancelledPaymentCount > 0
              ? `${result.cancelledPaymentCount} associated payment(s) cancelled`
              : undefined,
        });
        const paymentNote =
          result.cancelledPaymentCount > 0
            ? ` ${result.cancelledPaymentCount} associated payment${
                result.cancelledPaymentCount === 1 ? "" : "s"
              } cancelled.`
            : "";
        toastSuccess(`Invoice cancelled.${paymentNote}`);
      } else {
        const actor = resolvePerformedBy(loggedPerson, tokenDecoded);
        await cancelAccountingPayment(cancelDoc, office, actor);
        notifyBillingChanged();
        setCancelState(null);
        void logEvent({
          billingDocId: cancelDoc.id,
          billingDocType: "payment",
          partyKey: cancelDoc.partyKey,
          partyId: office.id,
          officeId,
          firmId,
          invoiceNumber: cancelDoc.reference,
          action: "cancelled",
        });
        toastSuccess("Payment cancelled.");
      }
    } catch (error) {
      console.error("Error cancelling billing document:", error);
      toastError(
        cancelState.docType === "invoice"
          ? "Unable to cancel invoice. Please try again."
          : "Unable to cancel payment. Please try again.",
      );
    } finally {
      setCancelling(false);
    }
  }, [cancelState, office, officeId, firmId, logEvent, loggedPerson, tokenDecoded]);

  const handleConfirmSend = useCallback(async () => {
    if (!sendState) {
      return;
    }
    const { docId, docType, isTest } = sendState;
    const testRecipients = isTest
      ? selectedStaff
          .map(
            (option) =>
              staffContacts.find((contact) => contact.id === option.id)?.email ??
              "",
          )
          .filter(Boolean)
      : undefined;

    if (isTest && testRecipients?.length === 0) {
      toastError("Select at least one staff recipient.");
      return;
    }

    setSending(true);
    try {
      const history = await getOfficeBillingHistory(officeId);
      const doc = history.find((d) => d.id === docId);
      if (!doc) {
        throw new Error("Document not found.");
      }

      if (docType === "invoice") {
        const result = await sendAccountingInvoice(doc, office, {
          test: isTest,
          testRecipients,
        });
        if (!isTest) {
          await upsertInvoice({
            ...doc,
            timesSent: (Number(doc.timesSent) || 0) + 1,
            isSent: true,
          } as Parameters<typeof upsertInvoice>[0]);
          notifyBillingChanged();
        }
        void logEvent({
          billingDocId: doc.id,
          billingDocType: "invoice",
          partyKey: doc.partyKey,
          partyId: office.id,
          officeId,
          firmId,
          invoiceNumber: doc.invoiceNumber,
          contextKind: doc.context?.kind,
          action: "sent",
          sendMode: isTest ? "test" : "real",
          recipients: result.sentTo ? result.sentTo.split(", ").filter(Boolean) : [],
          detail: `sent to ${result.sentTo}`,
        });
        toastSuccess(
          `${isTest ? "Test invoice" : "Invoice"} sent to ${result.sentTo}.`,
        );
      } else {
        const result = await sendAccountingPayment(doc, office, {
          test: isTest,
          testRecipients,
          history,
        });
        if (!isTest) {
          await upsertPayment({
            ...doc,
            isSent: true,
          });
          notifyBillingChanged();
        }
        void logEvent({
          billingDocId: doc.id,
          billingDocType: "payment",
          partyKey: doc.partyKey,
          partyId: office.id,
          officeId,
          firmId,
          invoiceNumber: doc.reference,
          action: "sent",
          sendMode: isTest ? "test" : "real",
          recipients: result.sentTo ? result.sentTo.split(", ").filter(Boolean) : [],
          detail: `sent to ${result.sentTo}`,
        });
        toastSuccess(
          `${
            isTest ? "Test payment receipt" : "Payment receipt"
          } sent to ${result.sentTo}.`,
        );
      }

      setSendState(null);
      setSendRecipients(null);
      setSelectedStaff([]);
      setStaffContacts([]);
    } catch (error) {
      console.error("Error sending accounting document:", error);
      toastError("Unable to send. Please try again.");
    } finally {
      setSending(false);
    }
  }, [sendState, office, officeId, firmId, selectedStaff, staffContacts, logEvent]);

  const invoiceActionsCell = useCallback(
    ({ row }: IHeaderCellProps) => (
      <AccountingInvoiceActionsCell
        row={row as BillingHistoryDoc}
        onPreviewEdit={handlePreviewEdit}
        onDownload={handleDownloadInvoice}
        onSend={handleSend}
        onSendTest={handleSendTest}
        onAddPayment={handleAddPayment}
        onCancel={handleOpenCancel}
        onPreviewEditPayment={handlePreviewEditPayment}
        onDownloadPayment={handleDownloadPayment}
        onSendPayment={handleSendPayment}
        onSendTestPayment={handleSendTestPayment}
        downloading={downloadingInvoiceId === row.id}
        canSendBilling={canSendBilling}
        sendBillingDisabledReason={sendBillingDisabledReason}
      />
    ),
    [
      handlePreviewEdit,
      handleDownloadInvoice,
      handleSend,
      handleSendTest,
      handleAddPayment,
      handleOpenCancel,
      handlePreviewEditPayment,
      handleDownloadPayment,
      handleSendPayment,
      handleSendTestPayment,
      downloadingInvoiceId,
      canSendBilling,
      sendBillingDisabledReason,
    ],
  );

  const ledgerDateCell = useCallback(
    ({ row }: IHeaderCellProps) => (
      <span>{String(row.ledgerDate ?? "—")}</span>
    ),
    [],
  );

  const columnsDef = useMemo<IHeader[]>(
    () => [
      {
        id: "ledgerSortAt",
        header: "Date",
        width: 50,
        cell: ledgerDateCell,
      },
      { id: "ledgerType", header: "Type", width: 70, enableSorting: false },
      { id: "reference", header: "Reference", width: 80 },
      { id: "description", header: "Description", width: 100 },
      { id: "charge", header: "Charge", width: 80, enableSorting: false },
      { id: "credit", header: "Payment", width: 80, enableSorting: false },
      { id: "balance", header: "Balance", width: 80, enableSorting: false },
      { id: "status", header: "Status", width: 90, enableSorting: false },
      {
        id: "actions",
        header: "Actions",
        width: 150,
        enableSorting: false,
        cell: invoiceActionsCell,
      },
    ],
    [invoiceActionsCell, ledgerDateCell],
  );

  const officeLabel = officeDisplayName(office);
  const excelFilename = useMemo(() => {
    const safeName =
      officeLabel
        .replace(/[^\w\s-]/g, "")
        .replace(/\s+/g, " ")
        .trim() || "office";
    return `${safeName}-billing-history-${activeTab}.xlsx`;
  }, [officeLabel, activeTab]);

  const toolbarButtons = (
    <div className="flex flex-wrap items-center justify-end gap-2">
      <Button
        text="New Invoice"
        color="primary"
        variant="filled"
        size="fixed"
        onClick={() => setCreateOpen(true)}
      />
      <Button
        text="Register Payment"
        color="primary"
        variant="outlined"
        size="fixed"
        onClick={() => {
          setPaymentInvoiceId(null);
          setPaymentOpen(true);
        }}
      />
    </div>
  );

  return (
    <div className="flex min-w-0 flex-col gap-4 border-t border-gray-200 pt-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <h2 className={SECTION_TITLE_CLASS.replace("mb-4", "mb-0")}>
            Invoicing &amp; Payment History
          </h2>
          {totals.recordCount > 0 && (
            <span className={FEE_TYPE_LABEL_CLASS}>
              {totals.recordCount} record{totals.recordCount === 1 ? "" : "s"}
            </span>
          )}
        </div>
      </div>

      <AccountingModalTabs
        tabs={LEDGER_HISTORY_TABS}
        active={activeTab}
        onChange={setActiveTab}
      />

      <FilteredTableCore
        key={`billing-history-${officeId}-${activeTab}-${billingRefreshKey}`}
        tableKey={`/accounting/${officeId}/invoicing-payment-history`}
        columnsDef={columnsDef}
        showRowNumber
        showTopHorizontalScrollBar
        endpoint="/billing"
        filtersData={[]}
        formatData={formatData}
        persistentFilter={persistentFilter}
        defaultSorting={LEDGER_HISTORY_DEFAULT_SORTING}
        seedDefaultSortingPreference
        onSortingChange={setActiveSorting}
        defaultPageSize={200}
        persistPageSize={false}
        persistFilters={false}
        showSearchInput
        searchPlaceholder="Search by reference or description"
        searchToolbarAccessory={toolbarButtons}
        containerclassName="!px-0"
        emptyStateMessage={
          activeTab === "all"
            ? "No invoices or payments found for this office."
            : activeTab === "annual"
              ? "No annual invoices or related payments found for this office."
              : "No assessment invoices or related payments found for this office."
        }
        excelFilename={excelFilename}
      />

      {totals.recordCount > 0 && (
        <div className="bg-white pb-4">
          <div className="flex flex-wrap items-baseline gap-x-10 gap-y-3 border-t-2 border-gray-300 py-4 md:py-5">
            <span className={`mr-auto ${TOTALS_TITLE_CLASS}`}>Totals</span>
            <span className={TOTALS_LABEL_CLASS}>
              Charged:{" "}
              <span className={TOTALS_VALUE_CLASS}>
                {formatMoneyAlways(totals.totalCharged)}
              </span>
            </span>
            <span className={TOTALS_LABEL_CLASS}>
              Paid:{" "}
              <span className={TOTALS_VALUE_CLASS}>
                {formatMoneyAlways(totals.totalPaid)}
              </span>
            </span>
            <span className={TOTALS_LABEL_CLASS}>
              Balance:{" "}
              <span className={TOTALS_VALUE_CLASS}>
                {formatMoneyAlways(totals.totalCharged - totals.totalPaid)}
              </span>
            </span>
          </div>
        </div>
      )}

      <CreateInvoiceModal
        office={office}
        isOpen={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreated={notifyBillingChanged}
      />
      <CreatePaymentModal
        office={office}
        isOpen={paymentOpen}
        onClose={handleClosePaymentModal}
        onRegistered={notifyBillingChanged}
        initialInvoiceId={paymentInvoiceId}
      />
      <EditInvoiceModal
        office={office}
        invoice={editInvoice}
        isOpen={editInvoice != null}
        onClose={() => setEditInvoice(null)}
        onSaved={notifyBillingChanged}
      />
      <EditPaymentModal
        office={office}
        payment={editPayment}
        isOpen={editPayment != null}
        onClose={() => setEditPayment(null)}
        onSaved={notifyBillingChanged}
      />
      <SendBillingDocModal
        isOpen={sendState != null}
        docType={sendState?.docType ?? "invoice"}
        isTest={sendState?.isTest ?? false}
        sendRecipients={sendRecipients}
        sendRecipientsLoading={sendRecipientsLoading}
        staffContacts={staffContacts}
        staffContactsLoading={staffContactsLoading}
        selectedStaff={selectedStaff}
        onSelectedStaffChange={setSelectedStaff}
        isSubmitting={sending}
        onCancel={handleCancelSend}
        onConfirm={handleConfirmSend}
      />
      <CancelBillingDocModal
        isOpen={cancelState != null}
        docType={cancelState?.docType ?? "invoice"}
        docRef={
          cancelState?.docType === "payment"
            ? cancelState.doc.reference ??
              cancelState.doc.id ??
              ""
            : cancelState?.doc.invoiceNumber ??
              cancelState?.doc.id ??
              ""
        }
        linkedPaymentCount={cancelState?.linkedPaymentCount ?? 0}
        isSubmitting={cancelling}
        onCancel={handleDismissCancel}
        onConfirm={handleConfirmCancel}
      />
    </div>
  );
}
